/*
 *
 * EditTerminalPopup
 *
 * @version 1.0.0
 * @author Andrea Pinardi
 * @since 13-Jan-2025
 */

import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Box } from '@mui/system'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import MerchantModal from '@nexidigital/merchantmodal'
import PortalRadioBox from '@nexidigital/portalradiobox'
import PortalSelect from '@nexidigital/portalselect'
import PortalTextInput from '@nexidigital/portaltextinput'
import { readOnlyControl, userPermissionControl } from 'Utility/globalMethods'
import FormField from 'Components/FormField'
import PortalTextBlocked from 'Components/PortalTextBlocked'
import formattedMessage, { messages } from '../TerminalConfiguration/languageModule'
import './EditTerminalPopup.scss'

const EditTerminalPopup = ({ terminal, userPermissions, actions, isUpdatingTerminal }) => {
  const intl = useIntl()
  const formik = useFormik({
    initialValues: {
      terminalId: terminal?.terminalId || '',
      emailNotificationMerchant: String(terminal?.emailNotificationMerchant) || 'false',
      transactionNotifyEmail: terminal?.transactionNotifyEmail || '',
      emailNotificationCardholder: String(terminal?.emailNotificationCardholder) || 'false',
      captureType: terminal?.captureType || '',
      deferredCaptureDays: terminal?.deferredCaptureDays ? String(terminal?.deferredCaptureDays) : '',
      requestorURL: terminal?.baseUrl || ''
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: Yup.object({
      transactionNotifyEmail: Yup.string()
        .email(formattedMessage.validEmail)
        .when('emailNotificationMerchant', {
          is: 'true',
          then: Yup.string().required(formattedMessage.validEmail),
          otherwise: Yup.string().notRequired()
        }),
      requestorURL: Yup.string()
        .url(formattedMessage.requestorURLError)
        .test('is-https', formattedMessage.requestorURLError, (value) => {
          if (!value) return false
          return value.startsWith('https://') || value.startsWith('http://')
        })
    })
  })

  const onSubmit = (values) => {
    actions.doUpdateTerminalConfig(values)
  }

  useEffect(() => {
    // revalidate form when emailNotificationMerchant changes
    // to remove any error message under the transactionNotifyEmail and requestorURL field and enable save button
    formik.validateForm()
  }, [formik.values.emailNotificationMerchant, formik.values.requestorURL])

  const yesNoOptions = [
    { value: false, label: intl.formatMessage(messages.no) },
    { value: true, label: intl.formatMessage(messages.yes) }
  ]

  const enabledDisabledOptions = [
    { value: false, label: intl.formatMessage(messages.disabled) },
    { value: true, label: intl.formatMessage(messages.enabled) }
  ]

  const captureTypes = ['IMPLICIT', 'EXPLICIT', 'DEFERRED'].map((v) => ({
    label: intl.formatMessage(messages[v.toLowerCase()]),
    value: v
  }))
  const deferredCaptureDays = Array.from('01234567', (x) => ({ label: x, value: x }))

  return (
    <MerchantModal
      closeLabel={intl.formatMessage(messages.close)}
      bigPopup
      topBarTitle={formattedMessage.title(terminal?.terminalId)}
      onClose={() => actions.closePopup()}
      primaryButtonLabel={formattedMessage.confirm}
      primaryButtonDisabled={
        !formik.isValid || (formik.values.captureType === 'DEFERRED' && !formik.values.deferredCaptureDays)
      }
      primaryButtonLoading={isUpdatingTerminal}
      primaryAction={() => onSubmit(formik.values)}>
      <MerchantGenericCard>
        <Box className='terminalConfiguration_cardContent'>
          <Box className='terminalConfiguration_paymentNotifications'>
            <h3 className='terminalConfiguration_paymentNotifications_subtitle'>
              {formattedMessage.paymentNotifications}
            </h3>
            <div className='form-row'>
              <FormField>
                <Box className='terminalConfiguration_paymentNotifications_field emailNotificationMerchant'>
                  {/* temporary PortalRadioBox label with tooltip | TODO - UPDATE COMPONENT IN LIBRARY */}
                  <PortalTextBlocked
                    label={formattedMessage.promptPaymentNotifications}
                    value={' '}
                    textTooltip={intl.formatMessage(messages.promptPaymentNotifications)}
                    id='radioBoxLabel_emailNotifications'
                    noPadding
                  />
                  <PortalRadioBox
                    radioGroupName='emailNotificationMerchant'
                    row
                    options={yesNoOptions.map((option) => ({
                      ...option,
                      id: `emailNotificationMerchant_${option.value}`
                    }))}
                    defaultValue={formik.values.emailNotificationMerchant}
                    onChange={(e) => {
                      formik.setFieldValue('emailNotificationMerchant', e)
                      if (e === 'false') {
                        formik.setFieldValue('transactionNotifyEmail', formik.initialValues.transactionNotifyEmail)
                      }
                    }}
                    centered
                  />
                </Box>
              </FormField>
              <FormField
                readOnly={formik.values.emailNotificationMerchant === 'false'}
                readOnlyComponent={
                  <PortalTextBlocked
                    label={formattedMessage.notificationEmail}
                    value={terminal?.transactionNotifyEmail || '-'}
                    noPadding
                  />
                }>
                <Box className='terminalConfiguration_paymentNotifications_field email'>
                  <PortalTextInput
                    ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
                    placeholder=' '
                    label={intl.formatMessage(messages.email)}
                    id='transactionNotifyEmail'
                    autoComplete='transaction-notify-email'
                    type='email'
                    error={formik.errors.transactionNotifyEmail}
                    errorText={formattedMessage.validEmail}
                    {...formik.getFieldProps('transactionNotifyEmail')}
                  />
                </Box>
              </FormField>
              <FormField
                hide={!userPermissionControl('settings.terminals.cardholderEmailNotification', userPermissions)}
                readOnly={readOnlyControl('settings.terminals.cardholderEmailNotification', userPermissions)}
                readOnlyComponent={
                  <PortalTextBlocked
                    label={formattedMessage.customerPaymentNotifications}
                    value={terminal?.emailNotificationCardholder ? formattedMessage.enabled : formattedMessage.disabled}
                    textTooltip={intl.formatMessage(messages.tooltipCustomerNotifications)}
                    id='radioBoxLabel_customerNotifications'
                    noPadding
                  />
                }>
                <Box className='terminalConfiguration_paymentNotifications_field'>
                  {/* temporary PortalRadioBox label with tooltip | TODO - UPDATE COMPONENT IN LIBRARY */}
                  <PortalTextBlocked
                    label={formattedMessage.promptCustomerPaymentNotifications}
                    value={' '}
                    textTooltip={intl.formatMessage(messages.tooltipCustomerNotifications)}
                    id='radioBoxLabel_customerNotifications'
                    noPadding
                  />
                  <PortalRadioBox
                    radioGroupName='emailNotificationCardholder'
                    row
                    options={enabledDisabledOptions.map((option) => ({
                      ...option,
                      id: `emailNotificationCardholder_${option.value}`
                    }))}
                    defaultValue={formik.values.emailNotificationCardholder}
                    onChange={(e) => formik.setFieldValue('emailNotificationCardholder', e)}
                    centered
                  />
                </Box>
                <Box className='terminalConfiguration_paymentNotifications_field'>
                  <PortalTextInput
                    ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
                    label={intl.formatMessage(messages.requestorURL)}
                    id='3DSRequestorURLInput'
                    autoComplete='3DS-Requestor-URL '
                    type='text'
                    error={formik.errors.requestorURL}
                    errorText={formattedMessage.requestorURLError}
                    {...formik.getFieldProps('requestorURL')}
                  />
                </Box>
              </FormField>
            </div>
          </Box>
          {userPermissionControl('settings.terminals.captureType', userPermissions) && (
            <Box className='terminalConfiguration_paymentExecution'>
              <h3 className='terminalConfiguration_paymentExecution_subtitle'>{formattedMessage.paymentExecution}</h3>
              <div className='form-row'>
                <FormField
                  hide={!userPermissionControl('settings.terminals.captureType', userPermissions)}
                  readOnly={readOnlyControl('settings.terminals.captureType', userPermissions)}
                  readOnlyComponent={
                    <PortalTextBlocked
                      label={formattedMessage.confirmationType}
                      value={intl.formatMessage(messages[terminal?.captureType?.toLowerCase()]) || '-'}
                      infoToShow={[
                        {
                          title: intl.formatMessage(messages.tooltipCustomerConfirmationTypeTitle)
                        },
                        {
                          title: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList1Title),
                          value: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList1Description)
                        },
                        {
                          title: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList2Title),
                          value: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList2Description)
                        },
                        {
                          title: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList3Title),
                          value: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList3Description)
                        },
                        {
                          title: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList4Title),
                          value: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList4Description)
                        }
                      ]}
                      textTooltip={`${intl.formatMessage(
                        messages.tooltipCustomerConfirmationTypeTitle
                      )} ${intl.formatMessage(messages.tooltipCustomerConfirmationTypeList1Title)}${intl.formatMessage(
                        messages.tooltipCustomerConfirmationTypeList1Description
                      )} ${intl.formatMessage(messages.tooltipCustomerConfirmationTypeList2Title)}${intl.formatMessage(
                        messages.tooltipCustomerConfirmationTypeList2Description
                      )} ${intl.formatMessage(messages.tooltipCustomerConfirmationTypeList3Title)}${intl.formatMessage(
                        messages.tooltipCustomerConfirmationTypeList3Description
                      )}`}
                      noPadding
                    />
                  }>
                  <Box className='terminalConfiguration_paymentExecution_field captureType'>
                    <PortalSelect
                      id='captureType'
                      options={captureTypes}
                      placeholder=' '
                      labelIcon='info'
                      label={formattedMessage.confirmationType}
                      ariaLabelIconButton={intl.formatMessage(messages.confirmationType)}
                      infoToShow={[
                        {
                          title: intl.formatMessage(messages.tooltipCustomerConfirmationTypeTitle)
                        },
                        {
                          title: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList1Title),
                          value: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList1Description)
                        },
                        {
                          title: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList2Title),
                          value: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList2Description)
                        },
                        {
                          title: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList3Title),
                          value: intl.formatMessage(messages.tooltipCustomerConfirmationTypeList3Description)
                        }
                      ]}
                      textTooltip={`${intl.formatMessage(
                        messages.tooltipCustomerConfirmationTypeTitle
                      )} ${intl.formatMessage(messages.tooltipCustomerConfirmationTypeList1Title)}${intl.formatMessage(
                        messages.tooltipCustomerConfirmationTypeList1Description
                      )} ${intl.formatMessage(messages.tooltipCustomerConfirmationTypeList2Title)}${intl.formatMessage(
                        messages.tooltipCustomerConfirmationTypeList2Description
                      )} ${intl.formatMessage(messages.tooltipCustomerConfirmationTypeList3Title)}${intl.formatMessage(
                        messages.tooltipCustomerConfirmationTypeList3Description
                      )}`}
                      noSearch
                      value={formik.values.captureType}
                      onChange={(option) => {
                        formik.setFieldValue('captureType', option.value)
                        if (option.value !== 'DEFERRED') formik.setFieldValue('deferredCaptureDays', null)
                      }}
                    />
                  </Box>
                </FormField>
                <FormField hide={formik.values.captureType !== 'DEFERRED'}>
                  <Box className='terminalConfiguration_paymentExecution_field deferredCaptureDays'>
                    <PortalSelect
                      id='deferredCaptureDays'
                      options={deferredCaptureDays}
                      placeholder=' '
                      labelIcon=''
                      label={formattedMessage.deferredDays}
                      ariaLabelIconButton={intl.formatMessage(messages.deferredDays)}
                      noSearch
                      value={formik.values.deferredCaptureDays?.toString()}
                      onChange={(option) => formik.setFieldValue('deferredCaptureDays', option.value)}
                    />
                  </Box>
                </FormField>
              </div>
            </Box>
          )}
        </Box>
      </MerchantGenericCard>
    </MerchantModal>
  )
}

EditTerminalPopup.propTypes = {
  // Add here some propTypes
  // and set a minimal description i.e.
  /** Description of prop "baz". */
  terminal: PropTypes.object,
  userPermissions: PropTypes.array,
  actions: PropTypes.object,
  isUpdatingTerminal: PropTypes.bool
}

EditTerminalPopup.defaultProps = {
  // Add here some default propTypes values
  terminal: {},
  userPermissions: [],
  actions: {},
  isUpdatingTerminal: false
}

export default EditTerminalPopup
